// Copyright © 2026 Alex Bruce. All rights reserved.
// Real-time collaborative editing via Supabase Realtime.
// Uses Broadcast for low-latency cell sync, Presence for cursor tracking,
// and Postgres Changes as a consistency backstop.

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { CollaboratorPresence, Division, ItemState } from "../types";

// ── Color palette for collaborators ─────────────────────────────────────
const COLLAB_COLORS = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4",
  "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F",
];

function pickColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length];
}

// ── Broadcast message types ─────────────────────────────────────────────
interface CellUpdate {
  type: "cell_update";
  itemId: string;
  field: string;
  value: any;
  userId: string;
  ts: number;
}

interface ScalarUpdate {
  type: "scalar_update";
  field: string;
  value: any;
  userId: string;
  ts: number;
}

interface StructureUpdate {
  type: "structure_update";
  divisions: Division[];
  itemState: Record<string, ItemState>;
  userId: string;
  ts: number;
}

type BroadcastPayload = CellUpdate | ScalarUpdate | StructureUpdate;

// ── Hook ────────────────────────────────────────────────────────────────
interface UseRealtimeSyncOptions {
  projectId: string | undefined;
  userId: string;
  displayName: string;
  enabled: boolean;
}

interface UseRealtimeSyncReturn {
  collaborators: CollaboratorPresence[];
  trackCell: (itemId: string | null, field: string | null) => void;
  broadcastCellUpdate: (itemId: string, field: string, value: any) => void;
  broadcastScalarUpdate: (field: string, value: any) => void;
  broadcastStructureUpdate: (divisions: Division[], itemState: Record<string, ItemState>) => void;
  isConnected: boolean;
}

export function useRealtimeSync({
  projectId,
  userId,
  displayName,
  enabled,
}: UseRealtimeSyncOptions): UseRealtimeSyncReturn {
  const [collaborators, setCollaborators] = useState<CollaboratorPresence[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<any>(null);
  const lastBroadcastTs = useRef(0);
  const structureBroadcastTimer = useRef<any>(null);

  // Callback refs for remote change handlers (set by the consumer)
  const onCellUpdateRef = useRef<((u: CellUpdate) => void) | null>(null);
  const onScalarUpdateRef = useRef<((u: ScalarUpdate) => void) | null>(null);
  const onStructureUpdateRef = useRef<((u: StructureUpdate) => void) | null>(null);
  const onPostgresSyncRef = useRef<((payload: any) => void) | null>(null);

  // ── Channel lifecycle ──
  useEffect(() => {
    if (!enabled || !projectId || !userId || !supabase) {
      setCollaborators([]);
      setIsConnected(false);
      return;
    }

    const channelName = `project:${projectId}`;
    const channel = supabase.channel(channelName, {
      config: { presence: { key: userId } },
    });

    // ── Presence ──
    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState();
      const collabs: CollaboratorPresence[] = [];
      for (const [key, presences] of Object.entries(state)) {
        if (key === userId) continue; // skip self
        const p = (presences as any[])[0];
        if (p) {
          collabs.push({
            userId: p.userId || key,
            displayName: p.displayName || "User",
            color: p.color || pickColor(key),
            activeItemId: p.activeItemId || null,
            activeField: p.activeField || null,
          });
        }
      }
      setCollaborators(collabs);
    });

    // ── Broadcast ──
    channel.on("broadcast", { event: "edit" }, ({ payload }: { payload: BroadcastPayload }) => {
      if (!payload || payload.userId === userId) return; // skip own echoes
      switch (payload.type) {
        case "cell_update":
          onCellUpdateRef.current?.(payload);
          break;
        case "scalar_update":
          onScalarUpdateRef.current?.(payload);
          break;
        case "structure_update":
          onStructureUpdateRef.current?.(payload);
          break;
      }
    });

    // ── Postgres Changes (consistency backstop) ──
    channel.on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "projects", filter: `id=eq.${projectId}` },
      (payload: any) => {
        onPostgresSyncRef.current?.(payload);
      }
    );

    // ── Subscribe ──
    channel.subscribe(async (status: string) => {
      if (status === "SUBSCRIBED") {
        setIsConnected(true);
        await channel.track({
          userId,
          displayName,
          color: pickColor(userId),
          activeItemId: null,
          activeField: null,
        });
      } else if (status === "CLOSED" || status === "CHANNEL_ERROR") {
        setIsConnected(false);
      }
    });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      setIsConnected(false);
      setCollaborators([]);
    };
  }, [projectId, userId, displayName, enabled]);

  // ── Track active cell (presence update) ──
  const trackCell = useCallback(
    (itemId: string | null, field: string | null) => {
      const ch = channelRef.current;
      if (!ch) return;
      ch.track({
        userId,
        displayName,
        color: pickColor(userId),
        activeItemId: itemId,
        activeField: field,
      });
    },
    [userId, displayName]
  );

  // ── Broadcast helpers ──
  const sendBroadcast = useCallback(
    (payload: BroadcastPayload) => {
      const ch = channelRef.current;
      if (!ch) return;
      // Throttle: 100ms minimum between broadcasts
      const now = Date.now();
      if (now - lastBroadcastTs.current < 100) return;
      lastBroadcastTs.current = now;
      ch.send({ type: "broadcast", event: "edit", payload });
    },
    []
  );

  const broadcastCellUpdate = useCallback(
    (itemId: string, field: string, value: any) => {
      sendBroadcast({ type: "cell_update", itemId, field, value, userId, ts: Date.now() });
    },
    [sendBroadcast, userId]
  );

  const broadcastScalarUpdate = useCallback(
    (field: string, value: any) => {
      sendBroadcast({ type: "scalar_update", field, value, userId, ts: Date.now() });
    },
    [sendBroadcast, userId]
  );

  const broadcastStructureUpdate = useCallback(
    (divisions: Division[], itemState: Record<string, ItemState>) => {
      // Debounce structure updates (larger payload)
      if (structureBroadcastTimer.current) clearTimeout(structureBroadcastTimer.current);
      structureBroadcastTimer.current = setTimeout(() => {
        const ch = channelRef.current;
        if (!ch) return;
        ch.send({
          type: "broadcast",
          event: "edit",
          payload: { type: "structure_update", divisions, itemState, userId, ts: Date.now() },
        });
      }, 300);
    },
    [userId]
  );

  return {
    collaborators,
    trackCell,
    broadcastCellUpdate,
    broadcastScalarUpdate,
    broadcastStructureUpdate,
    isConnected,
    // Expose refs for handler registration (consumer sets these)
    _onCellUpdateRef: onCellUpdateRef,
    _onScalarUpdateRef: onScalarUpdateRef,
    _onStructureUpdateRef: onStructureUpdateRef,
    _onPostgresSyncRef: onPostgresSyncRef,
  } as UseRealtimeSyncReturn & {
    _onCellUpdateRef: typeof onCellUpdateRef;
    _onScalarUpdateRef: typeof onScalarUpdateRef;
    _onStructureUpdateRef: typeof onStructureUpdateRef;
    _onPostgresSyncRef: typeof onPostgresSyncRef;
  };
}
